/**
 * Shared .leedoc export builder
 *
 * Provides common export logic used by:
 * - BrowserStorageBackend (IndexedDB)
 * - CloudStorageBackend (Cloud API)
 * - backup-cli.ts (Server-side SQLite)
 *
 * Works in both browser and Node.js environments.
 */

import JSZip from 'jszip';
import { weave, tangle, cleanForExport, extractMediaReferences } from './index.js';

/**
 * Input data for building a .leedoc export
 */
export interface ExportProjectData {
  /** Project name */
  name: string;
  /** Source files (path -> content) */
  files: Array<{ path: string; content: string }>;
  /** History snapshots */
  history: Array<{
    filePath: string;
    id: string;
    timestamp: number;
    reason: string | null;
    content: string;
  }>;
  /** Media files */
  media: Array<{
    filename: string;
    originalName: string;
    mimeType: string;
    size: number;
    width: number | null;
    height: number | null;
    uploadedBy: string | null;
    data: ArrayBuffer | Uint8Array | Buffer | Blob;
  }>;
}

/**
 * Options for the export builder
 */
export interface ExportBuilderOptions {
  /** Callback for logging warnings (optional) */
  onWarning?: (message: string) => void;
}

/**
 * Result from building the export
 */
export interface ExportBuilderResult {
  /** The JSZip instance with all content added */
  zip: JSZip;
  /** Statistics about the export */
  stats: {
    filesProcessed: number;
    scriptsGenerated: number;
    historySnapshots: number;
    mediaFiles: number;
  };
}

/**
 * Build the contents of a .leedoc export ZIP
 *
 * This function handles all the common export logic:
 * - Adding project.json metadata
 * - Adding source files and components
 * - Processing files through weave/tangle
 * - Generating _generated/docs/ and _generated/scripts/
 * - Adding _history/ snapshots
 * - Adding _media/ with manifest and copying to _images/
 *
 * @param data - Project data to export
 * @param options - Optional settings
 * @returns JSZip instance ready for generateAsync()
 */
export async function buildProjectExport(
  data: ExportProjectData,
  options: ExportBuilderOptions = {}
): Promise<ExportBuilderResult> {
  const { onWarning = () => {} } = options;
  const zip = new JSZip();
  const stats = {
    filesProcessed: 0,
    scriptsGenerated: 0,
    historySnapshots: 0,
    mediaFiles: 0,
  };

  // Add project metadata
  zip.file('project.json', JSON.stringify({
    name: data.name,
    version: '1.0.0',
    lastModified: Date.now(),
    leedocVersion: '1.0'
  }, null, 2));

  // Build components map from files with _components/ prefix
  const components: Record<string, string> = {};
  for (const file of data.files) {
    if (file.path.startsWith('_components/')) {
      const componentName = file.path.replace('_components/', '');
      components[componentName] = file.content;
    }
  }

  // Add all source files to ZIP
  for (const file of data.files) {
    zip.file(file.path, file.content);
  }

  // Build media lookup map
  const mediaByFilename = new Map(data.media.map(m => [m.filename, m]));

  // Track which media files are used by each category
  const mediaUsedByCategory = new Map<string, Set<string>>();

  // List of all source-page paths — used by wikilink expansion to emit relative links
  const allPagePaths = data.files
    .filter(f => f.path.endsWith('.src.md') && !f.path.startsWith('_components/'))
    .map(f => f.path);

  // Process each .src.md file through weave/tangle
  for (const file of data.files) {
    if (!file.path.endsWith('.src.md')) continue;

    try {
      const weaveData = weave(file.content, {
        components,
        sourcePath: file.path,
        allPaths: allPagePaths,
      });

      if (weaveData.success && weaveData.output) {
        stats.filesProcessed++;

        // Extract media references before cleaning for export
        const mediaRefs = extractMediaReferences(weaveData.output);
        const category = file.path.split('/')[0] || 'uncategorized';

        // Track media used by this category
        if (mediaRefs.length > 0) {
          if (!mediaUsedByCategory.has(category)) {
            mediaUsedByCategory.set(category, new Set());
          }
          for (const ref of mediaRefs) {
            mediaUsedByCategory.get(category)!.add(ref);
          }
        }

        // Save woven markdown to _generated/docs/
        // cleanForExport transforms media:// URLs to ./_images/ relative paths
        const docPath = file.path.replace('.src.md', '.md');
        const cleanDocs = cleanForExport(weaveData.output);
        zip.file(`_generated/docs/${docPath}`, cleanDocs);

        // Tangle to extract scripts
        const tangleData = tangle(weaveData.output);

        if (tangleData.success && tangleData.scripts) {
          const baseName = file.path.split('/').pop()?.replace('.src.md', '') ?? 'file';
          for (const [scriptName, scriptContent] of Object.entries(tangleData.scripts)) {
            zip.file(`_generated/scripts/${category}/${baseName}/${scriptName}`, scriptContent);
            stats.scriptsGenerated++;
          }
        }
      }
    } catch (err) {
      onWarning(`Failed to generate outputs for ${file.path}: ${err}`);
    }
  }

  // Copy media files to _images folders for each category that uses them
  for (const [category, usedMedia] of mediaUsedByCategory) {
    for (const filename of usedMedia) {
      const media = mediaByFilename.get(filename);
      if (media) {
        zip.file(`_generated/docs/${category}/_images/${filename}`, media.data);
      }
    }
  }

  // Add file-level version history snapshots
  for (const snapshot of data.history) {
    zip.file(`_history/${snapshot.filePath}/${snapshot.id}.json`, JSON.stringify({
      timestamp: snapshot.timestamp,
      reason: snapshot.reason
    }, null, 2));
    zip.file(`_history/${snapshot.filePath}/${snapshot.id}.md`, snapshot.content);
    stats.historySnapshots++;
  }

  // Export media files to _media/ folder
  if (data.media.length > 0) {
    // Add media manifest
    const manifest = data.media.map(m => ({
      filename: m.filename,
      originalName: m.originalName,
      mimeType: m.mimeType,
      size: m.size,
      width: m.width,
      height: m.height,
      uploadedBy: m.uploadedBy,
    }));
    zip.file('_media/manifest.json', JSON.stringify(manifest, null, 2));

    // Add each media file
    for (const media of data.media) {
      zip.file(`_media/${media.filename}`, media.data);
      stats.mediaFiles++;
    }
  }

  return { zip, stats };
}

/**
 * Generate a .leedoc file as a Node.js Buffer
 */
export async function generateExportBuffer(
  data: ExportProjectData,
  options: ExportBuilderOptions = {}
): Promise<Buffer> {
  const { zip } = await buildProjectExport(data, options);
  return await zip.generateAsync({
    type: 'nodebuffer',
    compression: 'DEFLATE',
    compressionOptions: { level: 9 }
  });
}

/**
 * Generate a .leedoc file as a Blob (for browser download)
 */
export async function generateExportBlob(
  data: ExportProjectData,
  options: ExportBuilderOptions = {}
): Promise<Blob> {
  const { zip } = await buildProjectExport(data, options);
  return await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 9 }
  });
}
